import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import toast from "react-hot-toast";
import type { FeaturedBadgeRule, FeaturedBadgeRuleFormValues, FeaturedBadgeRuleGroup, FeaturedBadgeRuleEntryValues } from "../../../models/rulesFeaturedBadge";
import {
  useCreateFeaturedBadgeRulesMutation,
  useDeleteFeaturedBadgeRuleGroupMutation,
  useDeleteFeaturedBadgeRulesEntriesMutation,
  useGetFeaturedBadgeRulesQuery,
  useToggleFeaturedBadgeRulesMutation,
  useUpdateFeaturedBadgeRulesMutation,
} from "../../../store/services/rules-featured-badge";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import { useTenantScope } from "../../../hooks/useTenantScope";
import {
  applyOwnedRulesTenantId,
  findGrupsTenantIdFromList,
  isRulesTenantOwnedByAdmin,
  ownedRulesTenantIdForAdmin,
} from "../constants/rulesTenant";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorMessage,
  getApiErrorStatus,
  getApiValidationIssues,
} from "../../../utils/functions/api-errors";
import { featuredBadgeTabConfig, mapFeaturedBadgeRuleToFormValues } from "../configs/featuredBadgeTabConfig";
import {
  buildFeaturedBadgeCreatePayload,
  buildFeaturedBadgeGroupScopePayload,
  buildFeaturedBadgeGroupTogglePayload,
  buildFeaturedBadgeGroupScopePayloadFromForm,
  buildFeaturedBadgeUpdatePayload,
  isFeaturedBadgeEntryDirty,
  matchesFeaturedBadgeSubTypeFilter,
  matchesFeaturedBadgeTenantFilter,
  matchesFeaturedBadgeTypeFilter,
  sortFeaturedBadgeEntriesByUrgency,
  validateFeaturedBadgeSubmission,
} from "../configs/featuredBadgeRuleHelpers";
import type { AdminRulesCategoryPanelProps, AdminRulesFilterState } from "../types";
import { BadgeRulesDataTable } from "./BadgeRulesDataTable";
import { FeaturedBadgeRulesFormPanel } from "./FeaturedBadgeRulesFormPanel";
import type { RulesInlineFormMode } from "./RulesInlineFormPanel";

const RULES_LIST_LOAD_ERROR = "No se pudieron cargar las reglas de badges destacados.";

const getFormSubmitErrorMessage = (submitError: unknown, fallback: string): string => {
  const issues = getApiValidationIssues(submitError) ?? [];
  const issueMessage = issues
    .map((issue) => (typeof issue.message === "string" ? issue.message.trim() : ""))
    .find(Boolean);

  if (issueMessage) {
    return issueMessage;
  }

  return getApiErrorMessage(submitError) ?? fallback;
};

export const FeaturedBadgeRulesTabContent = ({
  createTrigger = 0,
  onFormVisibilityChange,
}: AdminRulesCategoryPanelProps) => {
  const [filters, setFilters] = useState<AdminRulesFilterState>({});
  const [formMode, setFormMode] = useState<RulesInlineFormMode>("hidden");
  // We no longer track a single editing row ID, because we edit the entire group
  const [formValues, setFormValues] = useState<FeaturedBadgeRuleFormValues>(
    featuredBadgeTabConfig.getEmptyFormValues()
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [editingScopeRowIds, setEditingScopeRowIds] = useState<number[]>([]);

  const { data, isLoading, isError, error, refetch } = useGetFeaturedBadgeRulesQuery();
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const ownedTenantId = ownedRulesTenantIdForAdmin(tenantScope.role, tenantScope.tenantId);
  const grupsTenantId = useMemo(() => findGrupsTenantIdFromList(tenants), [tenants]);
  const [createFeaturedBadgeRules, { isLoading: isCreating }] =
    useCreateFeaturedBadgeRulesMutation();
  const [updateFeaturedBadgeRules, { isLoading: isUpdating }] =
    useUpdateFeaturedBadgeRulesMutation();
  const [deleteFeaturedBadgeRulesEntries, { isLoading: isDeletingEntry }] =
    useDeleteFeaturedBadgeRulesEntriesMutation();
  const [deleteRuleGroup, { isLoading: isDeletingGroup }] = useDeleteFeaturedBadgeRuleGroupMutation();
  const [toggleFeaturedBadgeRules] = useToggleFeaturedBadgeRulesMutation();
  const togglingGroupKeysRef = useRef<Set<string>>(new Set());
  const isSubmittingRef = useRef(false);
  const editingInitialEntryIdsRef = useRef<number[]>([]);
  const editingInitialRowsRef = useRef<Map<number, FeaturedBadgeRule>>(new Map());

  const allRows = useMemo(() => data ?? [], [data]);
  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        if (!matchesFeaturedBadgeTypeFilter(row.eventTypeId, filters.eventTypeId)) {
          return false;
        }
        if (!matchesFeaturedBadgeSubTypeFilter(row.eventSubTypeId, filters.eventSubTypeId)) {
          return false;
        }
        if (!isRulesTenantOwnedByAdmin(row.tenantId, ownedTenantId)) {
          return false;
        }
        if (
          filters.tenantId != null &&
          !matchesFeaturedBadgeTenantFilter(row.tenantId, filters.tenantId, grupsTenantId)
        ) {
          return false;
        }
        return true;
      }),
    [allRows, filters.eventSubTypeId, filters.eventTypeId, filters.tenantId, grupsTenantId, ownedTenantId]
  );
  const isSubmitting = isCreating || isUpdating || isDeletingEntry || isDeletingGroup;
  const isEditScreen = formMode === "edit";

  useEffect(() => {
    onFormVisibilityChange?.(isEditScreen);
    return () => onFormVisibilityChange?.(false);
  }, [isEditScreen, onFormVisibilityChange]);

  useEffect(() => {
    if (tenantScope.loading || tenantScope.locked) {
      return;
    }

    setFormMode("hidden");
    setFormError(null);
    setEditingScopeRowIds([]);
    editingInitialEntryIdsRef.current = [];
    editingInitialRowsRef.current = new Map();
  }, [tenantScope.loading, tenantScope.locked]);

  const openEditForm = (group: FeaturedBadgeRuleGroup) => {
    if (!tenantScope.locked) {
      return;
    }

    setFormMode("edit");

    const sameGroupRows = group.entries;

    const baseFormValues = mapFeaturedBadgeRuleToFormValues(sameGroupRows[0], grupsTenantId);
    baseFormValues.entries = sortFeaturedBadgeEntriesByUrgency(
      sameGroupRows.map((gr) => ({
        key: `entry-${gr.id}`,
        id: gr.id,
        label: gr.label,
        slug: gr.slug,
        thresholdMinutes: gr.thresholdMinutes,
        colorVariant: gr.colorVariant ?? 1,
      }))
    );

    editingInitialEntryIdsRef.current = sameGroupRows.map((row) => row.id);
    editingInitialRowsRef.current = new Map(sameGroupRows.map((row) => [row.id, row]));
    setEditingScopeRowIds(editingInitialEntryIdsRef.current);

    setFormValues(baseFormValues);
    setFormError(null);
  };

  const closeForm = () => {
    setFormMode("hidden");
    setFormError(null);
    setEditingScopeRowIds([]);
    editingInitialEntryIdsRef.current = [];
    editingInitialRowsRef.current = new Map();
  };

  const lastCreateTriggerRef = useRef(createTrigger);
  useEffect(() => {
    if (createTrigger > lastCreateTriggerRef.current && tenantScope.locked) {
      setFormMode("create");
      const emptyValues = applyOwnedRulesTenantId(
        featuredBadgeTabConfig.getEmptyFormValues(),
        ownedTenantId
      );
      if (!ownedTenantId && filters.tenantId != null) {
        emptyValues.tenantId = filters.tenantId;
      }
      if (filters.eventTypeId != null) {
        emptyValues.eventTypeId = filters.eventTypeId;
        if (filters.eventSubTypeId != null) {
          emptyValues.eventSubTypeId = filters.eventSubTypeId;
        }
      }
      setFormValues(emptyValues);
      setFormError(null);
      setEditingScopeRowIds([]);
      editingInitialEntryIdsRef.current = [];
      editingInitialRowsRef.current = new Map();
    }
    lastCreateTriggerRef.current = createTrigger;
  }, [
    createTrigger,
    filters.eventSubTypeId,
    filters.eventTypeId,
    filters.tenantId,
    ownedTenantId,
    tenantScope.locked,
  ]);

  const confirmDeleteGroup = (group: FeaturedBadgeRuleGroup, onAccepted: () => void) => {
    confirmDialog({
      message: "¿Eliminar esta regla y todas sus etiquetas?",
      header: "Eliminar regla",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: onAccepted,
    });
  };

  const handleDeleteGroup = async (
    scope: ReturnType<typeof buildFeaturedBadgeGroupScopePayload>
  ) => {
    if (isDeletingGroup) {
      return;
    }

    try {
      const result = await deleteRuleGroup(scope).unwrap();
      if (result.deletedCount === 0) {
        toast.error("No se encontró la regla para eliminar.");
        return;
      }

      toast.success("Regla eliminada");
      if (formMode !== "hidden") {
        closeForm();
      }
    } catch (deleteError) {
      toast.error(
        getAdminFacingApiErrorMessage(deleteError, "No se pudo eliminar la regla.")
      );
    }
  };

  const requestDeleteGroup = (group: FeaturedBadgeRuleGroup) => {
    if (!tenantScope.locked) {
      return;
    }

    confirmDeleteGroup(group, () => {
      void handleDeleteGroup(buildFeaturedBadgeGroupScopePayload(group));
    });
  };

  const handleDeleteFromEdit = () => {
    if (!tenantScope.locked) {
      return;
    }

    const scope = buildFeaturedBadgeGroupScopePayloadFromForm(formValues, grupsTenantId);
    confirmDeleteGroup(
      {
        groupKey: `${scope.tenantId ?? ""}|${scope.eventTypeId ?? ""}|${scope.eventSubTypeId ?? ""}`,
        id: editingScopeRowIds[0] ?? 0,
        tenantId: scope.tenantId,
        eventTypeId: scope.eventTypeId,
        eventSubTypeId: scope.eventSubTypeId,
        entries: [],
        active: Boolean(formValues.active),
      },
      () => {
        void handleDeleteGroup(scope);
      }
    );
  };

  const handleToggleActive = async (group: FeaturedBadgeRuleGroup) => {
    if (!tenantScope.locked) {
      return;
    }

    const nextActive = !group.entries.every((entry) => entry.active);

    if (togglingGroupKeysRef.current.has(group.groupKey)) {
      return;
    }

    togglingGroupKeysRef.current.add(group.groupKey);

    try {
      await toggleFeaturedBadgeRules(
        buildFeaturedBadgeGroupTogglePayload(group, nextActive)
      ).unwrap();
      toast.success(nextActive ? "Regla activada" : "Regla desactivada");
    } catch (toggleError) {
      toast.error(
        getAdminFacingApiErrorMessage(toggleError, "No se pudo actualizar el estado de la regla.")
      );
    } finally {
      togglingGroupKeysRef.current.delete(group.groupKey);
    }
  };

  const handleSubmit = async () => {
    if (!tenantScope.locked || isSubmittingRef.current) {
      return;
    }

    const submitValues = applyOwnedRulesTenantId(formValues, ownedTenantId);
    const excludedConflictRowIds =
      editingScopeRowIds.length > 0
        ? editingScopeRowIds
        : (submitValues.entries.map((entry) => entry.id).filter((id): id is number => id != null));
    const validationError = validateFeaturedBadgeSubmission(
      allRows,
      submitValues,
      excludedConflictRowIds,
      grupsTenantId
    );
    
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError(null);
    isSubmittingRef.current = true;

    try {
      const currentEntryIds = new Set(
        submitValues.entries
          .map((entry) => entry.id)
          .filter((id): id is number => id != null)
      );
      const deletedEntryIds = editingInitialEntryIdsRef.current.filter(
        (id) => !currentEntryIds.has(id)
      );

      if (deletedEntryIds.length > 0) {
        await deleteFeaturedBadgeRulesEntries({ ids: deletedEntryIds }).unwrap();
      }

      const entriesToUpdate = submitValues.entries
        .filter((entry): entry is FeaturedBadgeRuleEntryValues & { id: number } => entry.id != null)
        .filter((entry) => {
          const originalRow = editingInitialRowsRef.current.get(entry.id);
          return isFeaturedBadgeEntryDirty(entry, submitValues, originalRow, grupsTenantId);
        })
        .map((entry) => buildFeaturedBadgeUpdatePayload(entry, submitValues, entry.id, grupsTenantId));

      const entriesToCreate = submitValues.entries
        .filter((entry) => entry.id == null)
        .map((entry) => buildFeaturedBadgeCreatePayload(entry, submitValues, grupsTenantId));

      if (entriesToUpdate.length > 0) {
        await updateFeaturedBadgeRules({ entries: entriesToUpdate }).unwrap();
      }

      if (entriesToCreate.length > 0) {
        await createFeaturedBadgeRules({ entries: entriesToCreate }).unwrap();
      }

      toast.success("Regla guardada");
      closeForm();
    } catch (submitError) {
      const status = getApiErrorStatus(submitError);
      const numericStatus =
        typeof status === "number" ? status : Number.parseInt(String(status ?? ""), 10);
      const isValidationLikeError =
        numericStatus === 400 || numericStatus === 409 || numericStatus === 404;
      const message = isValidationLikeError
        ? getFormSubmitErrorMessage(
            submitError,
            "No se pudo guardar la regla. Revisá los datos ingresados."
          )
        : getAdminFacingApiErrorMessage(submitError, "No se pudo guardar la regla.");

      if (isValidationLikeError) {
        setFormError(message);
        toast(message, { icon: "⚠️" });
        return;
      }

      setFormError(message);
      toast.error(message);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  if (isEditScreen) {
    return (
      <div className="flex flex-column gap-3">
        <ConfirmDialog />
        <Button
          label="Volver al listado"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          className="align-self-start"
          onClick={closeForm}
          disabled={isSubmitting}
        />

        <FeaturedBadgeRulesFormPanel
          mode={formMode}
          values={formValues}
          onChange={setFormValues}
          onSubmit={handleSubmit}
          onCancel={closeForm}
          isSubmitting={isSubmitting}
          errorMessage={formError}
          existingRows={allRows}
          editingScopeRowIds={editingScopeRowIds}
          onDelete={handleDeleteFromEdit}
          isDeletingGroup={isDeletingGroup}
          grupsTenantId={grupsTenantId}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <ConfirmDialog />
      {formMode === "create" && (
        <FeaturedBadgeRulesFormPanel
          mode="create"
          values={formValues}
          onChange={setFormValues}
          onSubmit={handleSubmit}
          onCancel={closeForm}
          isSubmitting={isSubmitting}
          errorMessage={formError}
          existingRows={allRows}
          grupsTenantId={grupsTenantId}
        />
      )}

      {isError && (
        <div className="flex align-items-center justify-content-between p-3 border-round border-1 border-red-200 bg-red-50">
          <span className="text-red-600 font-medium">
            {getAdminFacingApiErrorMessage(error, RULES_LIST_LOAD_ERROR)}
          </span>
          <Button
            label="Reintentar"
            icon="pi pi-refresh"
            severity="danger"
            outlined
            size="small"
            onClick={() => refetch()}
          />
        </div>
      )}

      <BadgeRulesDataTable
        rows={rows}
        loading={isLoading && data === undefined}
        filters={filters}
        onFiltersChange={setFilters}
        onEdit={openEditForm}
        onDelete={requestDeleteGroup}
        onToggleActive={handleToggleActive}
        isDeleting={isDeletingGroup}
        mutationsDisabled={!tenantScope.locked}
      />
    </div>
  );
};
